import styled, { keyframes, css } from "styled-components";

// Animations
const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.45);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
`;

export const DetailsContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding-bottom: 40px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
`;

// Top Navigation & Command Bar
export const CommandBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
  padding: 12px 18px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.03);
  border: 1px solid rgba(226, 232, 240, 0.8);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const CommandLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 10px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
    transform: translateX(-2px);
  }
`;

export const RefPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 20px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #0f172a;
  }
`;

export const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  ${({ $status }) => {
    switch ($status) {
      case "accepted":
      case "completed":
        return css`
          background: #ecfdf5;
          color: #059669;
          border: 1px solid #a7f3d0;
        `;
      case "pending":
      case "live":
        return css`
          background: #eff6ff;
          color: #2563eb;
          border: 1px solid #bfdbfe;
        `;
      case "rejected":
      case "expired":
        return css`
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        `;
      default:
        return css`
          background: #f8fafc;
          color: #475569;
          border: 1px solid #e2e8f0;
        `;
    }
  }}
`;

export const ValuationMiniPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;

  ${({ $isAccepted }) =>
    $isAccepted
      ? css`
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          border: 1px solid #a7f3d0;
          color: #047857;
        `
      : css`
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #334155;
        `}

  .pill-label {
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 700;
    opacity: 0.85;
  }

  .pill-val {
    font-size: 15px;
    font-weight: 900;
  }
`;

// Executive Hero Canvas
export const HeroCanvas = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, #f1f5f9 100%);
  border-radius: 20px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 12px 36px -10px rgba(15, 23, 42, 0.06);
  padding: 24px 28px;
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 320px;
    height: 320px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 18px 16px;
  }
`;

export const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 1.2fr) minmax(240px, 1.1fr) minmax(240px, 1fr);
  gap: 24px;
  align-items: center;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

export const DeviceIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 540px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const DeviceThumbWrapper = styled.div`
  position: relative;
  width: 92px;
  height: 92px;
  flex-shrink: 0;
  border-radius: 16px;
  overflow: hidden;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);

  .ant-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }

  .photo-counter {
    position: absolute;
    bottom: 4px;
    right: 4px;
    background: rgba(15, 23, 42, 0.8);
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 8px;
    backdrop-filter: blur(4px);
  }
`;

export const DeviceInfo = styled.div`
  min-width: 0;
  flex: 1;

  .brand-chip {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #6366f1;
    margin-bottom: 2px;
  }

  .model-name {
    font-size: 20px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.25;
    margin-bottom: 8px;
    word-break: break-word;
  }

  .badges-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;
  }
`;

export const SpecPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: #f1f5f9;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
`;

export const ProfileStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 14px 18px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.03);

  .profile-row {
    display: flex;
    align-items: center;
    gap: 12px;

    &:not(:last-child) {
      padding-bottom: 8px;
      border-bottom: 1px solid #f1f5f9;
    }
  }

  .profile-avatar {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
    color: #4338ca;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 800;
    flex-shrink: 0;
  }

  .profile-info {
    min-width: 0;
    flex: 1;

    .label {
      font-size: 10px;
      font-weight: 700;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .name {
      font-size: 13px;
      font-weight: 700;
      color: #0f172a;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .meta-link {
      font-size: 11px;
      font-weight: 600;
      color: #2563eb;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 4px;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

export const ValuationHighlight = styled.div`
  padding: 18px 22px;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ $isAccepted }) =>
    $isAccepted
      ? css`
          background: linear-gradient(135deg, #064e3b 0%, #065f46 60%, #047857 100%);
          color: #ffffff;
          box-shadow: 0 10px 24px rgba(5, 150, 105, 0.22);
        `
      : css`
          background: linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #3730a3 100%);
          color: #ffffff;
          box-shadow: 0 10px 24px rgba(79, 70, 229, 0.2);
        `}

  .apex-label {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    opacity: 0.85;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }

  .apex-amount {
    font-size: 28px;
    font-weight: 900;
    letter-spacing: -0.5px;
    line-height: 1.1;
    margin-bottom: 6px;
    color: #ffffff;
  }

  .apex-meta {
    font-size: 12px;
    font-weight: 600;
    opacity: 0.85;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

// Section Header
export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  .section-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 800;
    color: #0f172a;
  }

  .section-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    background: ${({ $color }) => ($color ? `${$color}15` : "#f1f5f9")};
    color: ${({ $color }) => $color || "#334155"};
  }
`;

// Diagnostic & Inspection Matrix (Non-table, Non-boxed)
export const DiagnosticContainer = styled.div`
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.03);
  padding: 24px;
  margin-bottom: 24px;
`;

export const DiagnosticSummaryBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  margin-bottom: 16px;

  .score-label {
    font-size: 13px;
    font-weight: 700;
    color: #334155;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .filter-pills {
    display: flex;
    gap: 8px;
  }
`;

export const FilterPill = styled.button`
  padding: 5px 12px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? "#6366f1" : "#cbd5e1")};
  background: ${({ $active }) => ($active ? "#6366f1" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#475569")};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #6366f1;
    color: ${({ $active }) => ($active ? "#ffffff" : "#6366f1")};
  }
`;

export const DiagnosticGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;

export const DiagnosticCard = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid;
  transition: all 0.2s ease;

  ${({ $isPass }) =>
    $isPass
      ? css`
          background: #f0fdf4;
          border-color: #bbf7d0;

          &:hover {
            border-color: #86efac;
            box-shadow: 0 4px 12px rgba(34, 197, 94, 0.08);
          }
        `
      : css`
          background: #fef2f2;
          border-color: #fecaca;

          &:hover {
            border-color: #fca5a5;
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.08);
          }
        `}

  .question-body {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;

    .icon-wrap {
      font-size: 16px;
      margin-top: 2px;
      flex-shrink: 0;
      color: ${({ $isPass }) => ($isPass ? "#16a34a" : "#dc2626")};
    }

    .question-text {
      font-size: 13px;
      font-weight: 600;
      color: #1e293b;
      line-height: 1.35;
    }
  }

  .status-tag {
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    flex-shrink: 0;

    ${({ $isPass }) =>
      $isPass
        ? css`
            background: #dcfce7;
            color: #15803d;
          `
        : css`
            background: #fee2e2;
            color: #b91c1c;
          `}
  }
`;

// Sidebar Elements
export const SideCard = styled.div`
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.03);
  padding: 20px;
  margin-bottom: 20px;
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .info-key {
    color: #64748b;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .info-val {
    color: #0f172a;
    font-weight: 700;
    text-align: right;
  }
`;

export const LogisticsCard = styled.div`
  background: linear-gradient(135deg, #f0fdfa 0%, #e6fffa 100%);
  border: 1px solid #99f6e4;
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 16px;

  .agent-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;

    .agent-avatar {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      background: #0d9488;
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
    }

    .agent-name {
      font-size: 14px;
      font-weight: 800;
      color: #115e59;
    }

    .agent-phone {
      font-size: 12px;
      color: #0f766e;
      font-weight: 600;
    }
  }

  .meta-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;

    .meta-box {
      background: #ffffff;
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid #ccfbf1;

      .box-label {
        font-size: 10px;
        font-weight: 800;
        text-transform: uppercase;
        color: #0f766e;
      }

      .box-val {
        font-size: 13px;
        font-weight: 700;
        color: #134e4a;
        word-break: break-all;
      }
    }
  }
`;

export const ImageGalleryThumbStrip = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 4px 2px 10px;

  &::-webkit-scrollbar {
    height: 4px;
  }

  .gallery-thumb {
    width: 68px;
    height: 68px;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #6366f1;
      transform: scale(1.05);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;
